// TelcoTrack — cassette-bound direct pairs (KM4/KM6 weld trays).
// Primary "napřímo" is two incoming fibers spliced inside a tray cassette.
// Building-targeted directs stay secondary and keep `target`.

import {
  isRozvadecWeldTrayCassette,
  rozvadecWeldTraySeatCount,
  rozvadecWeldTraySeatLabel,
} from './rozvadec-cassette.constants';
import {
  isCompleteWeldIncomingRef,
  isCompleteWeldTargetRef,
  rozvadecWeldIncomingRefKey,
  trimId,
} from './rozvadec-weld-layout.ids';
import type {
  RozvadecWeldIncomingRef,
  RozvadecWeldLayout,
  RozvadecWeldLink,
} from './rozvadec-weld-layout.types';

/** Visual OS splice tray — UI only, never a row in `layout.cassettes`. */
export const ROZVADEC_OS_WELD_TRAY_ID = 'os-tray';

export function isRozvadecOsWeldTrayId(cassetteId: string | null | undefined): boolean {
  return trimId(cassetteId) === ROZVADEC_OS_WELD_TRAY_ID;
}

export function weldLinkCassetteId(link: Pick<RozvadecWeldLink, 'cassetteId'>): string {
  return trimId(link.cassetteId);
}

/** 1-based KM4/KM6 weld seat, or null when unset (historic pair / building direct). */
export function weldLinkSeat(link: Pick<RozvadecWeldLink, 'seat'>): number | null {
  return typeof link.seat === 'number' && Number.isInteger(link.seat) && link.seat >= 1
    ? link.seat
    : null;
}

export function weldLinkPeerIncoming(
  link: Pick<RozvadecWeldLink, 'peerIncoming'>,
): RozvadecWeldIncomingRef | null {
  return isCompleteWeldIncomingRef(link.peerIncoming) ? link.peerIncoming : null;
}

/** Direct weld hosted by a tray cassette (complete pair or pending first fiber). */
export function isRozvadecCassetteBoundDirect(link: RozvadecWeldLink): boolean {
  return (
    link.kind === 'direct' &&
    weldLinkCassetteId(link).length > 0 &&
    !isCompleteWeldTargetRef(link.target)
  );
}

/** Two incoming fibers spliced inside a KM4/KM6 cassette. */
export function isRozvadecCassettePairLink(link: RozvadecWeldLink): boolean {
  return (
    isRozvadecCassetteBoundDirect(link) &&
    isCompleteWeldIncomingRef(link.incoming) &&
    isCompleteWeldIncomingRef(link.peerIncoming)
  );
}

/** First incoming assigned to a tray; waiting for the mate. */
export function isRozvadecCassettePendingLink(link: RozvadecWeldLink): boolean {
  return (
    isRozvadecCassetteBoundDirect(link) &&
    isCompleteWeldIncomingRef(link.incoming) &&
    !isCompleteWeldIncomingRef(link.peerIncoming)
  );
}

export function isRozvadecBuildingTargetedLink(link: RozvadecWeldLink): boolean {
  return isCompleteWeldTargetRef(link.target);
}

export function isRozvadecBuildingDirectLink(link: RozvadecWeldLink): boolean {
  return link.kind === 'direct' && isCompleteWeldTargetRef(link.target);
}

/**
 * Cassette-bypass „napřímo“ / přímý spoj: incoming → building (or any direct
 * not hosted by a KM4/KM6 tray). Tray pairs stay cassette-bound.
 */
export function isRozvadecCassetteBypassDirect(link: RozvadecWeldLink): boolean {
  return link.kind === 'direct' && !isRozvadecCassetteBoundDirect(link);
}

/** Incoming fibers occupied by a direct link (building source and/or cassette pair). */
export function cassetteBoundIncomingRefs(link: RozvadecWeldLink): RozvadecWeldIncomingRef[] {
  if (link.kind !== 'direct') return [];
  const refs: RozvadecWeldIncomingRef[] = [];
  if (isCompleteWeldIncomingRef(link.incoming)) refs.push(link.incoming);
  if (isCompleteWeldIncomingRef(link.peerIncoming)) refs.push(link.peerIncoming);
  return refs;
}

/** Splitter output reserved for an incoming fiber on primary (no building yet). */
export function isRozvadecSplitterReservedLink(link: RozvadecWeldLink): boolean {
  return (
    link.kind === 'splitter' &&
    !isCompleteWeldTargetRef(link.target) &&
    isCompleteWeldIncomingRef(link.incoming)
  );
}

/** Every incoming fiber a link occupies (cassette pair sides + reserved splitter outputs). */
export function weldLinkIncomingRefs(link: RozvadecWeldLink): RozvadecWeldIncomingRef[] {
  if (link.kind === 'direct') return cassetteBoundIncomingRefs(link);
  if (isRozvadecSplitterReservedLink(link) && isCompleteWeldIncomingRef(link.incoming)) {
    return [link.incoming];
  }
  return [];
}

export function rozvadecCassettePairCount(
  layout: Pick<RozvadecWeldLayout, 'links'>,
): number {
  return layout.links.filter(isRozvadecCassettePairLink).length;
}

/** Complete cassette pairs + building-targeted directs consume tray weld slots. */
export function rozvadecCassetteWeldUsedCount(
  layout: Pick<RozvadecWeldLayout, 'links'>,
): number {
  return layout.links.filter(
    (link) => isRozvadecCassettePairLink(link) || isRozvadecBuildingDirectLink(link),
  ).length;
}

export function incomingRefsEqual(
  a: RozvadecWeldIncomingRef | null | undefined,
  b: RozvadecWeldIncomingRef | null | undefined,
): boolean {
  if (!isCompleteWeldIncomingRef(a) || !isCompleteWeldIncomingRef(b)) return false;
  return rozvadecWeldIncomingRefKey(a) === rozvadecWeldIncomingRefKey(b);
}

export function findPendingCassettePair(
  layout: Pick<RozvadecWeldLayout, 'links'>,
  cassetteId: string,
  seat?: number | null,
): RozvadecWeldLink | null {
  const id = cassetteId.trim();
  if (!id) return null;
  return (
    layout.links.find((link) => {
      if (!isRozvadecCassettePendingLink(link) || weldLinkCassetteId(link) !== id) return false;
      if (seat == null) return true;
      return weldLinkSeat(link) === seat;
    }) ?? null
  );
}

export type RozvadecWeldTraySeatRow = {
  seat: number;
  printLabel: string;
  linkId: string | null;
  pending: boolean;
};

/**
 * One row per weld seat on a provařovací kazeta (KM4 / KM6).
 * Visual order is top → bottom: seat 1 is first. Historic pairs without `seat`
 * fill the first free seats in link order.
 */
export function rozvadecWeldTraySeatRows(
  layout: Pick<RozvadecWeldLayout, 'cassettes' | 'links'>,
  cassetteId: string,
): RozvadecWeldTraySeatRow[] {
  const cassette = layout.cassettes.find((item) => item.id === cassetteId.trim()) ?? null;
  const count = rozvadecWeldTraySeatCount(cassette?.cassetteType);
  if (count === 0) return [];
  const bySeat = new Map<number, RozvadecWeldLink>();
  const unseated: RozvadecWeldLink[] = [];
  for (const link of linksForCassette(layout, cassetteId).filter(isRozvadecCassetteBoundDirect)) {
    const seat = weldLinkSeat(link);
    if (seat != null && seat >= 1 && seat <= count && !bySeat.has(seat)) {
      bySeat.set(seat, link);
    } else {
      unseated.push(link);
    }
  }
  let nextFree = 1;
  for (const link of unseated) {
    while (nextFree <= count && bySeat.has(nextFree)) nextFree += 1;
    if (nextFree > count) break;
    bySeat.set(nextFree, link);
    nextFree += 1;
  }
  const rows: RozvadecWeldTraySeatRow[] = [];
  for (let seat = 1; seat <= count; seat += 1) {
    const link = bySeat.get(seat) ?? null;
    rows.push({
      seat,
      printLabel: rozvadecWeldTraySeatLabel(seat),
      linkId: link?.id ?? null,
      pending: link ? isRozvadecCassettePendingLink(link) : false,
    });
  }
  return rows;
}

export function linksForCassette(
  layout: Pick<RozvadecWeldLayout, 'links'>,
  cassetteId: string,
): RozvadecWeldLink[] {
  const id = cassetteId.trim();
  if (!id) return [];
  return layout.links.filter((link) => weldLinkCassetteId(link) === id);
}

export function cassettePairTargetSortKey(link: RozvadecWeldLink): string {
  if (isCompleteWeldTargetRef(link.target)) {
    return `b:${link.target.buildingId.trim()}#${link.target.cableId.trim()}#${link.target.physicalFiber}`;
  }
  const peer = weldLinkPeerIncoming(link);
  return `c:${weldLinkCassetteId(link)}#${peer ? rozvadecWeldIncomingRefKey(peer) : 'pending'}`;
}

export function isWeldTrayCassetteType(cassetteType: string | null | undefined): boolean {
  return isRozvadecWeldTrayCassette(cassetteType);
}
